import { Button, Flex, Group, Stack, ThemeIcon, Title, useMantineColorScheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { TextSerif } from "@/components/elements";
import { IconArrowLeft } from "@/components/icons";

import type { MantineStyleProps } from "@mantine/core";
import type { FC, ReactNode } from "react";

export type PageHeaderProps = {
  badge?: ReactNode;
  title: string;
  children?: ReactNode;
  leftSection?: ReactNode;
  extraButton?: ReactNode;

  subtitle?: string;
} & MantineStyleProps &
  (
    | {
        showBack: true;
        backLabel?: string;
      }
    | {
        showBack: false;
        backLabel?: never;
      }
  );

export const PageHeader: FC<PageHeaderProps> = ({
  showBack,
  backLabel,
  badge,
  children,
  leftSection,
  extraButton,
  subtitle,
  title,
  ...props
}) => {
  const { colorScheme } = useMantineColorScheme();
  const color = colorScheme === "dark" ? "dark.0" : "dark.9";
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Stack gap="sm" {...props}>
      <Flex justify="space-between" align="center" gap="sm">
        {showBack && (
          <Group>
            <Button
              p={0}
              variant="transparent"
              onClick={() => navigate(-1)}
              aria-label={backLabel || t("common.back")}
            >
              <Group gap="sm">
                <ThemeIcon c={color} variant="transparent" size="sm">
                  <IconArrowLeft />
                </ThemeIcon>
                {backLabel || t("common.back")}
              </Group>
            </Button>
          </Group>
        )}

        {extraButton && extraButton}
      </Flex>
      <Group w="full" justify="space-between">
        <Group>
          {leftSection && leftSection}
          <Stack gap="xs">
            <Title
              fz={{
                base: "h2",
                md: "h1",
              }}
              style={{ overflowWrap: "anywhere" }}
            >
              {title}
            </Title>
            {(badge || subtitle) && (
              <Group>
                {subtitle && <TextSerif>{subtitle}</TextSerif>}
                {badge}
              </Group>
            )}
          </Stack>
        </Group>

        <Group>{children}</Group>
      </Group>
    </Stack>
  );
};
